import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { API, R_EVENTO } from "@/lib/api";
import { ArrowLeft, ChevronLeft, ChevronRight, Download, Loader2, Plus, ShieldAlert } from "lucide-react";

const FONT_MAIN = "'Hanken Grotesk', 'Inter', sans-serif";
const glassCard = {
  background: "rgba(40, 42, 50, 0.6)", backdropFilter: "blur(16px)", WebkitBackdropFilter: "blur(16px)",
  border: "1px solid rgba(141, 144, 160, 0.5)", borderTop: "1px solid rgba(255, 255, 255, 0.1)",
};
const inputClass = "w-full rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#b4c5ff] focus:ring-1 focus:ring-[#b4c5ff] transition-all placeholder:text-[#c3c6d7]/70";
const inputStyle = {
  background: "rgba(255, 255, 255, 0.1)", backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)",
  border: "1px solid rgba(255, 255, 255, 0.2)", color: "#e1e2ed", boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.3)",
  colorScheme: "dark",
};

const STATUS_R = {
  confirmada: { l: "Confirmada", c: "#7ef0a0" },
  cancelada: { l: "Cancelada", c: "#8d90a0" },
  concluida: { l: "Concluída", c: "#8ab4ff" },
};
const EMPTY_FORM = { area_id: "", data: "", inicio: "", fim: "", evento_tipo: "outro", convidados: 0, notas: "", termos: false };
const DIAS_SEM = ["D", "S", "T", "Q", "Q", "S", "S"];

function fmtDataBR(iso) {
  if (!iso) return "—";
  const d = new Date(String(iso).slice(0, 10) + "T12:00:00");
  if (isNaN(d)) return iso;
  return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" });
}
const fmtBRL = (v) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);
const hojeISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const hhmmOk = (v) => /^([01]\d|2[0-3]):[0-5]\d$/.test(v || "");
const toMin = (v) => { const [h, m] = v.split(":").map(Number); return h * 60 + m; };

function Chip({ ativo, onClick, children, testid, disabled }) {
  return (
    <button type="button" data-testid={testid} onClick={onClick} disabled={disabled}
      className="px-3 py-1.5 rounded-full text-xs font-medium transition-all active:scale-95 disabled:opacity-40"
      style={ativo
        ? { background: "rgba(180,197,255,0.18)", color: "#b4c5ff", border: "1px solid rgba(180,197,255,0.4)" }
        : { background: "rgba(255,255,255,0.05)", color: "#8d90a0", border: "1px solid rgba(255,255,255,0.1)" }}>
      {children}
    </button>
  );
}

function ResumoLinha({ label, value, testid }) {
  return (
    <div className="flex items-start justify-between gap-3 py-1.5">
      <span className="text-[11px] uppercase tracking-wider shrink-0" style={{ color: "#8d90a0" }}>{label}</span>
      <span className="text-xs font-medium text-right" style={{ color: "#e1e2ed" }} data-testid={testid}>{value}</span>
    </div>
  );
}

export default function MobileReservas({ origem, eql, cpf, nome, onClose }) {
  const [itens, setItens] = useState(null);
  const [tela, setTela] = useState("lista"); // lista | nova
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(EMPTY_FORM);
  const [areas, setAreas] = useState([]);
  const [disp, setDisp] = useState(null);
  const [cal, setCal] = useState(() => { const d = new Date(); return { ano: d.getFullYear(), mes: d.getMonth() + 1 }; });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [erro, setErro] = useState("");
  const [cancelId, setCancelId] = useState(null);
  const [cancelMotivo, setCancelMotivo] = useState("");

  const qs = `origem=${encodeURIComponent(origem)}&eql=${encodeURIComponent(eql)}&cpf=${cpf}`;

  const carregar = useCallback(async () => {
    try {
      const r = await fetch(`${API}/public/minhas-reservas?${qs}`);
      const d = await r.json();
      if (d.ok) setItens(d.itens); else setItens([]);
    } catch { setItens([]); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [origem, eql, cpf]);

  useEffect(() => { carregar(); }, [carregar]);

  useEffect(() => {
    if (tela !== "nova" || areas.length) return;
    fetch(`${API}/public/reservas-areas?origem=${encodeURIComponent(origem)}`)
      .then((r) => r.json()).then((d) => { if (d.ok) setAreas(d.areas); }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tela]);

  const mesParam = `${cal.ano}-${String(cal.mes).padStart(2, "0")}`;
  useEffect(() => {
    if (tela !== "nova" || !form.area_id) { setDisp(null); return; }
    fetch(`${API}/public/reservas-disponibilidade?origem=${encodeURIComponent(origem)}&area_id=${form.area_id}&mes=${mesParam}`)
      .then((r) => r.json()).then((d) => { if (d.ok) setDisp(d); }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tela, form.area_id, mesParam]);

  const dayInfo = form.data && disp ? disp.dias?.[form.data] : null;
  const areaSel = areas.find((a) => a.id === form.area_id);

  const diasGrid = useMemo(() => {
    const first = new Date(cal.ano, cal.mes - 1, 1);
    const total = new Date(cal.ano, cal.mes, 0).getDate();
    const cells = [];
    for (let i = 0; i < first.getDay(); i++) cells.push(null);
    for (let d = 1; d <= total; d++) cells.push(`${cal.ano}-${String(cal.mes).padStart(2, "0")}-${String(d).padStart(2, "0")}`);
    return cells;
  }, [cal]);

  const mudarMes = (delta) => {
    setCal((c) => {
      let m = c.mes + delta, a = c.ano;
      if (m < 1) { m = 12; a -= 1; }
      if (m > 12) { m = 1; a += 1; }
      return { ano: a, mes: m };
    });
  };

  const diaState = (iso) => {
    if (!iso) return null;
    if (iso < hojeISO()) return { off: true };
    const info = disp?.dias?.[iso];
    if (!info) return { off: false, loading: !disp };
    if (info.bloqueado) return { off: true, motivo: info.bloqueado };
    if (!info.periodos || info.periodos.length === 0) return { off: true };
    return { off: false, temReserva: (info.reservas || []).length > 0 };
  };

  const validarPasso1 = () => {
    if (!form.area_id || !form.data || !form.inicio || !form.fim) return "Selecione o espaço, a data e o horário da reserva.";
    if (!hhmmOk(form.inicio) || !hhmmOk(form.fim)) return "Horário inválido — use HH:MM.";
    if (form.fim <= form.inicio) return "O horário final deve ser maior que o inicial.";
    const info = disp?.dias?.[form.data];
    if (info) {
      if (info.bloqueado) return `Espaço bloqueado nesta data (${info.bloqueado}).`;
      const dentro = (info.periodos || []).some((p) => {
        const ini = Array.isArray(p) ? p[0] : p.inicio;
        const fim = Array.isArray(p) ? p[1] : p.fim;
        return form.inicio >= ini && form.fim <= fim;
      });
      if (!dentro) return "Fora da disponibilidade do espaço neste dia.";
      const conflito = (info.reservas || []).some((r) => r.inicio < form.fim && r.fim > form.inicio);
      if (conflito) return "Horário já reservado neste espaço.";
    }
    if (form.data === hojeISO()) {
      const agora = new Date();
      const minAnt = disp?.antecedencia_min || 0;
      if (toMin(form.inicio) < agora.getHours() * 60 + agora.getMinutes() + minAnt) {
        return minAnt ? `Horário inicial deve respeitar a antecedência mínima de ${minAnt} min.` : "O horário inicial já passou.";
      }
    }
    return null;
  };

  const proximo = () => {
    setErro("");
    if (step === 1) {
      const msg = validarPasso1();
      if (msg) { setErro(msg); return; }
    }
    if (step === 3 && !form.termos) { setErro("É obrigatório aceitar os termos de uso do espaço."); return; }
    if (step === 3) { setConfirmOpen(true); return; }
    setStep(step + 1);
  };

  const confirmarReserva = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${API}/public/reservas`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ origem, eql, cpf, ...form, convidados: Number(form.convidados) || 0 }),
      });
      const d = await r.json();
      if (d.ok) {
        toast.success(`Reserva #${String(d.item.numero).padStart(4, "0")} confirmada!`);
        setConfirmOpen(false); setTela("lista"); setStep(1); setForm(EMPTY_FORM);
        carregar();
      } else if (d.motivo === "agendamento") {
        setErro(d.detalhe || "Agendamento inválido.");
        setConfirmOpen(false);
      } else {
        setErro("Não foi possível gravar a reserva. Tente novamente.");
        setConfirmOpen(false);
      }
    } catch {
      setErro("Falha de comunicação. Tente novamente.");
      setConfirmOpen(false);
    } finally { setSaving(false); }
  };

  const cancelarReserva = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${API}/public/reservas/${cancelId}/cancelar`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ origem, eql, cpf, motivo: cancelMotivo }),
      });
      const d = await r.json();
      if (d.ok) {
        toast.success("Reserva cancelada.");
        setCancelId(null); setCancelMotivo("");
        carregar();
      } else {
        toast.error(
          d.motivo === "status" ? "Somente reservas confirmadas podem ser canceladas."
          : d.motivo === "data_passada" ? "Reservas passadas não podem ser canceladas."
          : "Não foi possível cancelar."
        );
      }
    } catch { toast.error("Falha de comunicação. Tente novamente."); }
    finally { setSaving(false); }
  };

  const nomeMes = new Date(cal.ano, cal.mes - 1, 1).toLocaleDateString("pt-BR", { month: "long", year: "numeric" });

  return (
    <div className="fixed inset-0 z-40 flex flex-col" style={{ background: "#11131b", fontFamily: FONT_MAIN }}
      data-testid="appmobile-reservas-overlay">
      <header className="flex items-center gap-3 px-4 py-3.5"
        style={{ borderBottom: "1px solid rgba(255,255,255,0.06)", paddingTop: "max(14px, env(safe-area-inset-top))" }}>
        <button type="button" data-testid="appmobile-reservas-voltar" aria-label="Voltar"
          onClick={() => (tela === "nova" ? (setTela("lista"), setErro("")) : onClose())}
          className="w-10 h-10 flex items-center justify-center rounded-xl active:bg-white/10"
          style={{ color: "#c3c6d7", touchAction: "manipulation" }}>
          <ArrowLeft className="h-5 w-5" />
        </button>
        <h1 className="text-base font-bold" style={{ color: "#b4c5ff" }} data-testid="appmobile-reservas-titulo">
          {tela === "nova" ? `Nova Reserva — Passo ${step} de 3` : "Minhas Reservas"}
        </h1>
        {tela === "lista" && (
          <button type="button" data-testid="appmobile-reservas-nova"
            onClick={() => { setTela("nova"); setStep(1); setForm(EMPTY_FORM); setErro(""); }}
            className="ml-auto flex items-center gap-1.5 rounded-xl px-3.5 py-2 text-xs font-semibold text-white active:scale-[0.97] transition-transform"
            style={{ background: "#2563EB", boxShadow: "0 0 12px rgba(37,99,235,0.3)" }}>
            <Plus className="h-4 w-4" /> Nova
          </button>
        )}
      </header>

      <main className="flex-1 overflow-y-auto px-4 py-4 pb-28">
        {tela === "lista" && (
          !itens ? (
            <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin" style={{ color: "#8d90a0" }} /></div>
          ) : itens.length === 0 ? (
            <p className="text-center text-xs py-16" style={{ color: "#8d90a0" }} data-testid="appmobile-reservas-vazio">
              Nenhuma reserva para a sua unidade.
            </p>
          ) : (
            <div className="flex flex-col gap-2" data-testid="appmobile-reservas-lista">
              {itens.map((r) => (
                <div key={r.id} className="rounded-xl px-3.5 py-3 relative overflow-hidden" style={glassCard}
                  data-testid={`appmobile-reserva-item-${r.id}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="flex-1 min-w-0 truncate text-xs font-semibold" style={{ color: "#e1e2ed" }}>{r.area_name}</span>
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full shrink-0"
                      style={{ background: `${(STATUS_R[r.status] || {}).c}22`, color: (STATUS_R[r.status] || {}).c }}
                      data-testid={`appmobile-reserva-status-${r.id}`}>{(STATUS_R[r.status] || {}).l || r.status}</span>
                  </div>
                  <p className="text-[11px] mt-1" style={{ color: "#c3c6d7" }}>
                    #{String(r.numero).padStart(4, "0")} · {fmtDataBR(r.data)} · {r.inicio}–{r.fim} · {R_EVENTO[r.evento_tipo] || r.evento_tipo}
                    {r.valor ? ` · ${fmtBRL(r.valor)}` : ""}
                  </p>
                  <p className="text-[11px] mt-1" style={{ color: "#8d90a0" }}
                    data-testid={`appmobile-reserva-solicitante-${r.id}`}>
                    Reservado por {r.solicitante_nome || "—"}
                  </p>
                  {(r.anexos || []).length > 0 && (
                    <div className="mt-2" data-testid={`appmobile-reserva-anexos-${r.id}`}>
                      <p className="text-[11px] mb-1.5" style={{ color: "#c3c6d7" }}>
                        Baixe aqui a documentação de Regras, Checklist e outros arquivos.
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {r.anexos.map((f) => (
                          <a key={f.id} data-testid={`appmobile-reserva-anexo-${r.id}-${f.id}`}
                            href={`${API}/public/reservas/${r.id}/anexos/${f.id}?${qs}`}
                            download={f.nome} target="_blank" rel="noreferrer"
                            className="inline-flex max-w-full items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-medium active:scale-95 transition-transform"
                            style={{ color: "#b4c5ff", background: "rgba(180,197,255,0.08)", border: "1px solid rgba(180,197,255,0.25)" }}>
                            <Download className="h-3 w-3 shrink-0" />
                            <span className="truncate">{f.nome}</span>
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                  {r.status === "cancelada" && r.motivo_cancelamento && (
                    <p className="text-[10px] mt-1" style={{ color: "#8d90a0" }}>Motivo: {r.motivo_cancelamento}</p>
                  )}
                  {r.status === "confirmada" && r.data >= hojeISO() && (
                    cancelId === r.id ? (
                      <div className="mt-2 flex flex-col gap-2" data-testid={`appmobile-reserva-cancelar-box-${r.id}`}>
                        <input data-testid="appmobile-reserva-cancel-motivo" value={cancelMotivo}
                          onChange={(e) => setCancelMotivo(e.target.value)} placeholder="Motivo (opcional)"
                          className={inputClass} style={inputStyle} />
                        <div className="flex gap-2">
                          <button type="button" data-testid="appmobile-reserva-cancel-voltar"
                            onClick={() => { setCancelId(null); setCancelMotivo(""); }}
                            className="flex-1 py-2 rounded-xl text-xs font-medium"
                            style={{ color: "#8d90a0", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)" }}>
                            Voltar
                          </button>
                          <button type="button" data-testid="appmobile-reserva-cancel-confirma" onClick={cancelarReserva} disabled={saving}
                            className="flex-1 py-2 rounded-xl text-xs font-semibold text-white disabled:opacity-40"
                            style={{ background: "#dc2626" }}>
                            {saving ? "Cancelando..." : "Confirmar cancelamento"}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button type="button" data-testid={`appmobile-reserva-cancelar-${r.id}`}
                        onClick={() => { setCancelId(r.id); setCancelMotivo(""); }}
                        className="mt-2 text-[11px] font-medium px-3 py-1.5 rounded-full active:scale-95 transition-transform"
                        style={{ color: "#ffb4ab", background: "rgba(255,180,171,0.08)", border: "1px solid rgba(255,180,171,0.3)" }}>
                        Cancelar reserva
                      </button>
                    )
                  )}
                </div>
              ))}
            </div>
          )
        )}

        {tela === "nova" && (
          <div className="flex flex-col gap-4" data-testid="appmobile-reservas-form">
            {step === 1 && (
              <>
                <div className="flex items-start gap-2 text-xs rounded-xl px-3 py-2.5"
                  style={{ background: "rgba(180,197,255,0.08)", color: "#b4c5ff", border: "1px solid rgba(180,197,255,0.25)" }}
                  data-testid="appmobile-reservas-aviso-passo1">
                  <ShieldAlert className="h-4 w-4 shrink-0 mt-px" />
                  <span>Atenção: a reserva nasce confirmada. No caso de erro a reserva deve ser cancelada e uma nova criada.</span>
                </div>
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-wider mb-2" style={{ color: "#c3c6d7" }}>Espaço</p>
                  <div className="flex flex-wrap gap-2" data-testid="appmobile-reservas-areas">
                    {areas.map((a) => (
                      <Chip key={a.id} testid={`appmobile-reserva-area-${a.id}`} ativo={form.area_id === a.id}
                        onClick={() => setForm({ ...form, area_id: a.id, data: "", inicio: "", fim: "" })}>
                        {a.code ? `${a.code} — ${a.name}` : a.name}
                      </Chip>
                    ))}
                  </div>
                </div>
                {form.area_id && (
                  <div className="rounded-xl p-3 relative overflow-hidden" style={glassCard} data-testid="appmobile-reservas-calendario">
                    <div className="flex items-center justify-between mb-2">
                      <button type="button" data-testid="appmobile-reservas-cal-prev" onClick={() => mudarMes(-1)}
                        className="w-8 h-8 flex items-center justify-center rounded-lg active:bg-white/10" style={{ color: "#c3c6d7" }}>
                        <ChevronLeft className="h-4 w-4" />
                      </button>
                      <span className="text-xs font-semibold capitalize" style={{ color: "#e1e2ed" }}>{nomeMes}</span>
                      <button type="button" data-testid="appmobile-reservas-cal-next" onClick={() => mudarMes(1)}
                        className="w-8 h-8 flex items-center justify-center rounded-lg active:bg-white/10" style={{ color: "#c3c6d7" }}>
                        <ChevronRight className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="grid grid-cols-7 gap-1 text-center">
                      {DIAS_SEM.map((d, i) => (
                        <span key={i} className="text-[10px] py-1" style={{ color: "#8d90a0" }}>{d}</span>
                      ))}
                      {diasGrid.map((iso, i) => {
                        if (!iso) return <span key={`v${i}`} />;
                        const st = diaState(iso);
                        const sel = form.data === iso;
                        return (
                          <button key={iso} type="button" data-testid={`appmobile-reserva-dia-${iso}`}
                            disabled={st?.off}
                            onClick={() => setForm({ ...form, data: iso, inicio: "", fim: "" })}
                            className="relative aspect-square rounded-lg text-xs font-medium transition-all active:scale-90 disabled:opacity-25"
                            style={sel
                              ? { background: "#2563EB", color: "#fff" }
                              : { color: "#c3c6d7", background: "rgba(255,255,255,0.04)" }}>
                            {Number(iso.slice(8))}
                            {st?.temReserva && !sel && (
                              <span className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full" style={{ background: "#f4a261" }} />
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
                {form.data && dayInfo && (
                  <p className="text-[11px]" style={{ color: "#8d90a0" }} data-testid="appmobile-reservas-faixas">
                    Disponível em {fmtDataBR(form.data)}: {(dayInfo.periodos || []).map((p) => Array.isArray(p) ? `${p[0]}–${p[1]}` : `${p.inicio}–${p.fim}`).join("  ·  ") || "sem horários"}
                    {(dayInfo.reservas || []).length > 0 && `  |  Ocupado: ${dayInfo.reservas.map((r) => `${r.inicio}–${r.fim}`).join(", ")}`}
                  </p>
                )}
                {form.data && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-[11px] font-semibold uppercase tracking-wider mb-1" style={{ color: "#c3c6d7" }}>Início</p>
                      <input type="time" data-testid="appmobile-reserva-inicio" value={form.inicio}
                        onChange={(e) => setForm({ ...form, inicio: e.target.value })} className={inputClass} style={inputStyle} />
                    </div>
                    <div>
                      <p className="text-[11px] font-semibold uppercase tracking-wider mb-1" style={{ color: "#c3c6d7" }}>Fim</p>
                      <input type="time" data-testid="appmobile-reserva-fim" value={form.fim}
                        onChange={(e) => setForm({ ...form, fim: e.target.value })} className={inputClass} style={inputStyle} />
                    </div>
                  </div>
                )}
              </>
            )}

            {step === 2 && (
              <>
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-wider mb-2" style={{ color: "#c3c6d7" }}>Tipo de evento</p>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(R_EVENTO).map(([k, label]) => (
                      <Chip key={k} testid={`appmobile-reserva-evento-${k}`} ativo={form.evento_tipo === k}
                        onClick={() => setForm({ ...form, evento_tipo: k })}>{label}</Chip>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-wider mb-1" style={{ color: "#c3c6d7" }}>Convidados</p>
                  <input type="number" min={0} max={2000} data-testid="appmobile-reserva-convidados" value={form.convidados}
                    onChange={(e) => setForm({ ...form, convidados: e.target.value })} className={inputClass} style={inputStyle} />
                </div>
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-wider mb-1" style={{ color: "#c3c6d7" }}>Observações</p>
                  <textarea rows={3} maxLength={500} data-testid="appmobile-reserva-notas" value={form.notas}
                    onChange={(e) => setForm({ ...form, notas: e.target.value })} placeholder="Opcional"
                    className={`${inputClass} resize-none`} style={inputStyle} />
                </div>
              </>
            )}

            {step === 3 && (
              <>
                <div className="rounded-xl p-4 relative overflow-hidden" style={glassCard} data-testid="appmobile-reservas-resumo-p3">
                  <ResumoLinha label="Espaço" value={areaSel ? `${areaSel.code ? areaSel.code + " — " : ""}${areaSel.name}` : "—"} testid="appmobile-reservas-r-area" />
                  <ResumoLinha label="Data" value={fmtDataBR(form.data)} testid="appmobile-reservas-r-data" />
                  <ResumoLinha label="Horário" value={`${form.inicio}–${form.fim}`} testid="appmobile-reservas-r-hora" />
                  <ResumoLinha label="Evento" value={R_EVENTO[form.evento_tipo]} />
                  <ResumoLinha label="Convidados" value={String(form.convidados || 0)} />
                  <ResumoLinha label="Taxa" value={fmtBRL(disp?.taxa_uso)} testid="appmobile-reservas-r-taxa" />
                </div>
                <label className="flex items-start gap-2.5 text-xs cursor-pointer" style={{ color: "#c3c6d7" }}
                  data-testid="appmobile-reserva-termos-label">
                  <input type="checkbox" data-testid="appmobile-reserva-termos" checked={form.termos}
                    onChange={(e) => setForm({ ...form, termos: e.target.checked })}
                    className="mt-0.5 w-4 h-4 accent-[#2563EB]" />
                  Confirmo que li e aceito as regras e os termos de uso do espaço. *
                </label>
              </>
            )}

            {erro && (
              <div data-testid="appmobile-reservas-erro"
                className="flex items-start gap-2 text-xs rounded-xl px-3 py-2.5"
                style={{ background: "rgba(255, 180, 171, 0.08)", color: "#ffb4ab", border: "1px solid rgba(255, 180, 171, 0.3)" }}>
                <ShieldAlert className="h-4 w-4 shrink-0 mt-px" />
                <span>{erro}</span>
              </div>
            )}

            <div className="flex gap-3">
              <button type="button" data-testid="appmobile-reservas-back" disabled={saving}
                onClick={() => { setErro(""); step === 1 ? setTela("lista") : setStep(step - 1); }}
                className="flex-1 py-3 rounded-xl text-sm font-medium active:scale-[0.98] transition-transform"
                style={{ color: "#8d90a0", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)" }}>
                Voltar
              </button>
              <button type="button" data-testid="appmobile-reservas-next" onClick={proximo} disabled={saving}
                className="flex-1 py-3 rounded-xl text-sm font-semibold text-white active:scale-[0.98] transition-all disabled:opacity-40"
                style={{ background: "#2563EB", boxShadow: "0 0 15px rgba(37,99,235,0.3)" }}>
                {step === 3 ? "Confirmar" : "Avançar"}
              </button>
            </div>
          </div>
        )}
      </main>

      {/* Modal de segunda confirmacao */}
      {confirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-6" data-testid="appmobile-reservas-resumo-modal">
          <div className="absolute inset-0" style={{ background: "rgba(0,0,0,0.7)" }} />
          <div className="relative w-full max-w-sm rounded-[14px] overflow-hidden" style={glassCard}>
            <div className="p-5">
              <h2 className="text-base font-bold text-center mb-3" style={{ color: "#e1e2ed" }}
                data-testid="appmobile-reservas-resumo-titulo">Confirmar Reserva</h2>
              <div className="rounded-xl px-3 py-1 mb-3" style={{ background: "rgba(10,11,16,0.55)", border: "1px solid rgba(255,255,255,0.06)" }}>
                <ResumoLinha label="Espaço" value={areaSel ? `${areaSel.code ? areaSel.code + " — " : ""}${areaSel.name}` : "—"} testid="appmobile-reservas-m-area" />
                <ResumoLinha label="Solicitante" value={`${nome} — ${eql}`} testid="appmobile-reservas-m-solicitante" />
                <ResumoLinha label="Data" value={fmtDataBR(form.data)} testid="appmobile-reservas-m-data" />
                <ResumoLinha label="Horário" value={`${form.inicio}–${form.fim}`} testid="appmobile-reservas-m-hora" />
                <ResumoLinha label="Evento" value={R_EVENTO[form.evento_tipo]} />
                <ResumoLinha label="Convidados" value={String(form.convidados || 0)} />
                {form.notas && <ResumoLinha label="Observações" value={form.notas} />}
                <ResumoLinha label="Taxa" value={fmtBRL(disp?.taxa_uso)} testid="appmobile-reservas-m-taxa" />
              </div>
              <div className="flex items-start gap-2 text-[11px] rounded-xl px-3 py-2.5 mb-4"
                style={{ background: "rgba(244,162,97,0.08)", color: "#f4a261", border: "1px solid rgba(244,162,97,0.3)" }}
                data-testid="appmobile-reservas-resumo-aviso">
                <ShieldAlert className="h-4 w-4 shrink-0 mt-px" />
                <span>Confirme as informações. No caso de erro a reserva deve ser cancelada e uma nova criada.</span>
              </div>
              <div className="flex gap-3">
                <button type="button" data-testid="appmobile-reservas-resumo-voltar" disabled={saving}
                  onClick={() => { setConfirmOpen(false); setStep(1); }}
                  className="flex-1 py-3 rounded-xl text-sm font-medium active:scale-[0.98] transition-transform"
                  style={{ color: "#8d90a0", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)" }}>
                  Voltar
                </button>
                <button type="button" data-testid="appmobile-reservas-resumo-confirmar" onClick={confirmarReserva} disabled={saving}
                  className="flex-1 py-3 rounded-xl text-sm font-semibold text-white active:scale-[0.98] transition-all disabled:opacity-40"
                  style={{ background: "#2563EB", boxShadow: "0 0 15px rgba(37,99,235,0.3)" }}>
                  {saving ? <Loader2 className="h-5 w-5 animate-spin mx-auto" /> : "Confirmar"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
